import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Image, Store, Calendar, LogOut, Menu, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const menuItems = [
    { name: 'Dashboard', path: '/admincclc/dashboard', icon: LayoutDashboard },
    { name: 'Banners', path: '/admincclc/banners', icon: Image },
    { name: 'Locales', path: '/admincclc/locales', icon: Store },
    { name: 'Eventos', path: '/admincclc/eventos', icon: Calendar },
  ];

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Sesión cerrada correctamente');
      navigate('/admincclc/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        <div className="flex h-screen overflow-hidden">
          <AnimatePresence>
            {(isSidebarOpen || window.innerWidth >= 768) && (
              <motion.aside
                initial={{ x: -300 }}
                animate={{ x: 0 }}
                exit={{ x: -300 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="fixed md:relative w-64 bg-secondary text-secondary-foreground h-full z-40 flex flex-col"
              >
                <div className="p-6 border-b border-white/10">
                  <img
                    src="/logolaconvencion.png"
                    alt="Logo"
                    className="h-12 brightness-0 invert"
                  />
                  <p className="text-sm text-secondary-foreground/70 mt-2">Panel Administrativo</p>
                </div>

                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsSidebarOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive(item.path)
                            ? 'bg-primary text-primary-foreground shadow-lg'
                            : 'hover:bg-white/10'
                          }`}
                      >
                        <Icon size={20} />
                        <span className="font-medium">{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>

                <div className="p-4 border-t border-white/10">
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="w-full justify-start gap-3 hover:bg-destructive hover:text-destructive-foreground"
                  >
                    <LogOut size={20} />
                    Cerrar Sesión
                  </Button>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          <div className="flex-1 flex flex-col overflow-hidden">
            <header className="bg-background border-b border-border p-4 flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </Button>

              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  {auth.currentUser?.email}
                </div>
              </div>
            </header>

            <main className="flex-1 overflow-y-auto p-6">
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLayout;